import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LegalLayout, LegalSection } from '../../components/LegalLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { api } from '../../lib/api';

/**
 * DsarPage — Modulo Data Subject Access Request.
 *
 * ★ 2026-08-27 (Phase 57.G) — Consente all'utente di esercitare i propri
 * diritti GDPR (accesso, rettifica, cancellazione, portabilità, opposizione,
 * limitazione, revoca del consenso). Salva la richiesta a backend +
 * invia email di conferma + notifica interna a Legal@.
 */
export default function DsarPage() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    request_type: 'access',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const REQUEST_TYPES = [
    'access', 'rectification', 'erasure', 'portability',
    'objection', 'restriction', 'withdraw_consent',
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    try {
      const lang = (i18n.language || 'it').split('-')[0];
      const res = await api.post('/public/dsar/request', {
        ...form,
        lang,
      });
      toast.success(t('legal.dsar.success', 'Richiesta ricevuta. Rif: {{id}}', { id: res.data.request_id.slice(0, 8) }));
      setForm({ full_name: '', email: '', request_type: 'access', message: '' });
    } catch (err) {
      toast.error(t('legal.dsar.error', "Errore nell'invio. Riprova."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalLayout
      docType="dsar"
      title={t('legal.dsar.pageTitle', 'Esercita i tuoi diritti')}
      intro={t('legal.dsar.intro', '')}
    >
      <form onSubmit={handleSubmit} className="mt-4 space-y-4" data-testid="dsar-form">
        <div>
          <Label htmlFor="dsar-name" className="text-xs uppercase tracking-widest text-muted-foreground">
            {t('legal.dsar.fields.fullName', 'Nome e Cognome')} *
          </Label>
          <Input
            id="dsar-name"
            required
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="mt-1"
            data-testid="dsar-input-name"
          />
        </div>

        <div>
          <Label htmlFor="dsar-email" className="text-xs uppercase tracking-widest text-muted-foreground">
            {t('legal.dsar.fields.email', 'Email')} *
          </Label>
          <Input
            id="dsar-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-1"
            data-testid="dsar-input-email"
          />
        </div>

        <div>
          <Label htmlFor="dsar-type" className="text-xs uppercase tracking-widest text-muted-foreground">
            {t('legal.dsar.fields.requestType', 'Tipo di richiesta')} *
          </Label>
          <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
            <SelectTrigger id="dsar-type" className="mt-1" data-testid="dsar-select-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REQUEST_TYPES.map((rt) => (
                <SelectItem key={rt} value={rt}>
                  {t(`legal.dsar.types.${rt}`, rt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="dsar-msg" className="text-xs uppercase tracking-widest text-muted-foreground">
            {t('legal.dsar.fields.message', 'Messaggio (facoltativo)')}
          </Label>
          <Textarea
            id="dsar-msg"
            rows={4}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="mt-1"
            data-testid="dsar-input-message"
            maxLength={2000}
          />
        </div>

        <Button
          type="submit"
          disabled={submitting || !form.full_name.trim() || !form.email.trim()}
          className="w-full"
          data-testid="dsar-submit"
        >
          {submitting ? t('legal.dsar.fields.submitting', 'Invio in corso…') : t('legal.dsar.fields.submit', 'Invia richiesta')}
        </Button>
      </form>
    </LegalLayout>
  );
}
